import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Zap, Users, BarChart3, Shield, Clock, Target, ArrowRight, ArrowLeft, CheckCircle2, Upload, X, ChevronRight, Menu, XIcon, Dumbbell, Heart, Flame, Sparkles, Building2, Wrench, Palette, Send, MonitorSmartphone, MessageSquare, Star, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import HuscleosHeader from '../components/huscleos/HuscleosHeader';

const API = process.env.REACT_APP_BACKEND_URL || '';

const C = {
  bg: '#FAFAFA', white: '#FFFFFF', dark: '#0F0F0F', darkSurface: '#161616',
  accent: '#FF2E00', text: '#1A1A1A', textMuted: '#6B7280', textLight: '#9CA3AF',
  border: '#E5E7EB',
};
const cn = (...a) => a.filter(Boolean).join(' ');
function FadeIn({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const [v, setV] = useState(false);
  useEffect(() => { const el = ref.current; if (!el) return; const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setV(true); o.disconnect(); } }, { threshold: 0.12 }); o.observe(el); return () => o.disconnect(); }, []);
  return <div ref={ref} className={cn(className, 'transition-all duration-700')} style={{ opacity: v ? 1 : 0, transform: v ? 'translateY(0)' : 'translateY(28px)', transitionDelay: `${delay}ms` }}>{children}</div>;
}

/* ── NAV ─────────────────────────────────────────────────── */
/* ── SOCIAL PROOF ─────────────────────────────────────────── */
const TICKER_MSGS = [
  { icon: '🔥', text: '27 gyms onboarded this month' },
  { icon: '⚡', text: '3-hour avg from quiz → live demo' },
  { icon: '💰', text: '$182K in owner revenue tracked in January' },
  { icon: '⭐', text: '4.9 / 5 avg operator rating' },
  { icon: '📈', text: '71% of demo gyms convert within 14 days' },
  { icon: '🏋️', text: '4 new CrossFit boxes went live this week' },
];

function LiveTicker() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % TICKER_MSGS.length), 3500);
    return () => clearInterval(t);
  }, []);
  const m = TICKER_MSGS[i];
  return (
    <div
      className="inline-flex items-center gap-3 px-4 py-2 border"
      style={{ borderColor: 'rgba(255,46,0,0.25)', background: 'rgba(255,46,0,0.06)', fontFamily: 'Manrope' }}
      data-testid="social-proof-ticker"
    >
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full rounded-full animate-ping opacity-75" style={{ background: C.accent }} />
        <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: C.accent }} />
      </span>
      <span className="text-[10px] font-bold tracking-widest uppercase" style={{ color: C.accent }}>LIVE</span>
      <span key={i} className="text-xs sm:text-sm font-semibold text-gray-800 transition-opacity duration-500 animate-[fadeInDown_0.4s_ease-out]">
        {m.icon} {m.text}
      </span>
    </div>
  );
}

function SocialProof() {
  const stats = [
    { num: '27+',   label: 'Gyms Onboarded' },
    { num: '$180K', label: 'Monthly Rev Processed' },
    { num: '4.9/5', label: 'Avg Owner Rating' },
    { num: '3 hr',  label: 'Avg Demo Setup' },
  ];
  const quotes = [
    { q: 'Went from 8 leads a month to 31. No more chasing paper.', who: 'Strength-first gym, Denver' },
    { q: 'Replaced MindBody + Mailchimp + my website guy. One bill.', who: 'CrossFit box, Austin' },
    { q: 'Our tour-to-member rate doubled in the first 60 days.', who: 'Boutique lifting studio, SoCal' },
  ];
  return (
    <section className="py-14 sm:py-16 border-t border-b" style={{ background: C.bg, borderColor: C.border }} data-testid="social-proof-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <FadeIn>
          <div className="flex flex-col items-center text-center mb-10">
            <LiveTicker />
            <p className="mt-4 text-xs font-bold tracking-[0.2em] uppercase" style={{ fontFamily: 'Manrope', color: C.textMuted }}>
              Trusted by independent operators across the US
            </p>
          </div>
        </FadeIn>

        <FadeIn delay={80}>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-10">
            {stats.map((s, i) => (
              <div key={i} className="bg-white px-5 py-6 border text-center" style={{ borderColor: C.border }} data-testid={`social-stat-${i}`}>
                <p className="text-3xl sm:text-4xl font-bold leading-none mb-2" style={{ fontFamily: 'Manrope', color: C.dark }}>{s.num}</p>
                <p className="text-[11px] font-semibold tracking-wide uppercase" style={{ fontFamily: 'Manrope', color: C.textMuted }}>{s.label}</p>
              </div>
            ))}
          </div>
        </FadeIn>

        <FadeIn delay={160}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
            {quotes.map((q, i) => (
              <div key={i} className="bg-white p-5 border relative" style={{ borderColor: C.border }} data-testid={`social-quote-${i}`}>
                <div className="flex items-center gap-1 mb-2">
                  {[...Array(5)].map((_, j) => <Star key={j} size={12} fill={C.accent} stroke={C.accent} />)}
                </div>
                <p className="text-sm leading-relaxed mb-3" style={{ fontFamily: 'Manrope', color: C.text }}>"{q.q}"</p>
                <p className="text-[11px] font-semibold tracking-wide uppercase" style={{ fontFamily: 'Manrope', color: C.textMuted }}>— {q.who}</p>
              </div>
            ))}
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

/* ── HERO ─────────────────────────────────────────────────── */
function Hero() {
  const scroll = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  return (
    <section id="hero" className="relative min-h-[92vh] flex items-center overflow-hidden" style={{ background: C.dark }}>
      <div className="absolute inset-0">
        <img src="https://images.pexels.com/photos/6389856/pexels-photo-6389856.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="w-full h-full object-cover" style={{ opacity: 0.35 }} loading="eager" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(15,15,15,0.85) 0%, rgba(15,15,15,0.6) 50%, rgba(15,15,15,0.9) 100%)' }} />
      </div>
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-24 sm:py-32">
        <FadeIn>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6 border border-white/20 bg-white/5 backdrop-blur-sm">
            <Sparkles size={14} style={{ color: C.accent }} />
            <span className="text-xs font-bold tracking-wide uppercase text-white/80" style={{ fontFamily: 'Manrope' }}>The Gym Operating System</span>
          </div>
        </FadeIn>
        <FadeIn delay={80}>
          <h1 className="text-5xl sm:text-7xl lg:text-8xl leading-[0.88] tracking-tighter uppercase mb-6" style={{ fontFamily: 'Bebas Neue', color: '#fff' }}>
            RUN THE GYM YOU'VE<br />BEEN TRYING TO <span style={{ color: C.accent }}>BUILD</span>.
          </h1>
        </FadeIn>
        <FadeIn delay={160}>
          <p className="text-base sm:text-lg leading-relaxed max-w-xl mb-2 text-gray-300" style={{ fontFamily: 'Manrope' }}>
            HuscleOS gives you the systems, structure, and flow of a high-performing gym — without the chaos, cost, or compromise.
          </p>
          <p className="text-sm font-bold mb-8 text-white" style={{ fontFamily: 'Manrope' }}>More members. Cleaner operations. Less guesswork.</p>
        </FadeIn>
        <FadeIn delay={240}>
          <div className="flex flex-wrap gap-4">
            <button onClick={() => scroll('quiz')} data-testid="hero-demo-cta" className="flex items-center gap-2 text-sm font-bold tracking-widest uppercase px-8 py-4 transition-all hover:shadow-lg hover:-translate-y-0.5" style={{ fontFamily: 'Manrope', background: C.accent, color: '#fff', boxShadow: '0 4px 24px rgba(255,46,0,0.35)' }}>BUILD MY DEMO <ArrowRight size={16} /></button>
            <button onClick={() => scroll('pricing')} data-testid="hero-setup-cta" className="flex items-center gap-2 text-sm font-bold tracking-widest uppercase px-8 py-4 border-2 border-white/40 transition-all hover:bg-white hover:text-black text-white" style={{ fontFamily: 'Manrope' }}>VIEW PLANS</button>
          </div>
        </FadeIn>
        <FadeIn delay={380}>
          <div className="mt-14 flex flex-wrap items-center gap-4 pt-8 border-t border-white/10">
            {[['Website + CRM', <MonitorSmartphone size={16} />], ['Automations', <Zap size={16} />], ['Campaigns', <Send size={16} />], ['Reviews', <Star size={16} />], ['Mobile Staff', <MessageSquare size={16} />]].map(([label, icon], i) => (
              <div key={i} className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/5">
                <span className="text-white/60">{icon}</span>
                <span className="text-xs font-medium text-white/70" style={{ fontFamily: 'Manrope' }}>{label}</span>
              </div>
            ))}
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

/* ── PAIN ─────────────────────────────────────────────────── */
function Pain() {
  return (
    <section className="py-20 sm:py-28" style={{ background: C.bg }}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <FadeIn><p className="text-sm font-bold tracking-[0.2em] uppercase mb-4" style={{ fontFamily: 'Manrope', color: C.accent }}>BE HONEST</p>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter uppercase leading-none mb-8" style={{ fontFamily: 'Bebas Neue', color: C.dark }}>THIS IS WHAT IT FEELS<br className="hidden sm:block" /> LIKE RIGHT NOW.</h2>
        </FadeIn>
        <div className="max-w-2xl space-y-5 mb-10">
          {[{ b: "You've got a website", r: "... but it doesn't really convert." }, { b: "Leads come in", r: "... but follow-up is inconsistent." }, { b: "Staff tries", r: "... but everyone does things differently." }, { b: "Your systems work", r: "... but not together." }].map((l, i) => (
            <FadeIn key={i} delay={i * 60}><p className="text-base leading-relaxed" style={{ fontFamily: 'Manrope', color: C.textMuted }}><span className="font-bold" style={{ color: C.text }}>{l.b}</span>{l.r}</p></FadeIn>
          ))}
        </div>
        <FadeIn delay={280}>
          <p className="text-base leading-relaxed mb-6" style={{ fontFamily: 'Manrope', color: C.textMuted }}>So growth feels harder than it should. Every month you're either paying more for tools that don't talk — or spending time trying to make them work.</p>
        </FadeIn>
        {/* Data-backed pain stats */}
        <FadeIn delay={340}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-5 border bg-white" style={{ borderColor: C.border }}>
            {[
              { stat: '25 hrs/wk', label: 'spent on manual data entry and reconciling across apps', src: 'Intuit' },
              { stat: '$3,000/mo', label: 'overspent on unused software by average small businesses', src: 'Intuit' },
              { stat: '96 min/day', label: 'of productivity lost juggling 4+ digital tools', src: 'Slack/Salesforce' },
            ].map((d, i) => (
              <div key={i} className="text-center sm:text-left p-2">
                <p className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Manrope', color: C.accent }}>{d.stat}</p>
                <p className="text-xs leading-relaxed mt-1" style={{ fontFamily: 'Manrope', color: C.muted }}>{d.label}</p>
                <p className="text-[10px] mt-1" style={{ color: C.light }}>Source: {d.src}</p>
              </div>
            ))}
          </div>
        </FadeIn>
        <FadeIn delay={400}>
          <p className="text-lg font-bold mt-6" style={{ fontFamily: 'Manrope', color: C.dark }}>That's not scalable. That's survival mode.</p>
        </FadeIn>
      </div>
    </section>
  );
}

/* ── FUTURE VISION ─────────────────────────────────────────── */
function Future() {
  return (
    <section className="py-20 sm:py-28" style={{ background: C.dark }}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <FadeIn><p className="text-sm font-bold tracking-[0.2em] uppercase mb-4" style={{ fontFamily: 'Manrope', color: C.accent }}>NOW PICTURE THIS</p>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter uppercase leading-none mb-10" style={{ fontFamily: 'Bebas Neue', color: '#fff' }}>THIS IS WHAT IT <span style={{ color: C.accent }}>COULD</span> FEEL LIKE.</h2>
        </FadeIn>
        <div className="space-y-3 mb-10">
          {[
            { text: 'Every lead gets responded to instantly.', data: 'Leads contacted in 5 min are 21x more likely to qualify. — MIT' },
            { text: 'Your website actually books tours and drives signups.', data: '50% of local searchers visit a store within a day. — Google' },
            { text: 'Your team works from one clean system.', data: 'Not 4+ tools losing you 96 minutes of productivity daily.' },
            { text: 'Follow-up happens automatically.', data: 'Waiting just 30 min drops contact odds by 100x. — MIT' },
            { text: 'Members don\'t fall through the cracks.', data: 'One retained member = ~$780/yr in dues. Compound that.' },
          ].map((item, i) => (
            <FadeIn key={i} delay={i * 50}><div className="flex items-start gap-4 p-4 border border-white/10 bg-white/5">
              <CheckCircle2 size={18} style={{ color: C.accent }} className="shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-200" style={{ fontFamily: 'Manrope' }}>{item.text}</p>
                <p className="text-xs text-gray-500 mt-1" style={{ fontFamily: 'Manrope' }}>{item.data}</p>
              </div>
            </div></FadeIn>
          ))}
        </div>
        <FadeIn delay={300}><p className="text-base text-gray-400 mb-1" style={{ fontFamily: 'Manrope' }}>Your gym runs smoother. Looks stronger. Grows faster.</p>
          <p className="text-lg font-bold" style={{ fontFamily: 'Manrope', color: C.accent }}>That's what HuscleOS is built for.</p>
        </FadeIn>
      </div>
    </section>
  );
}

/* ── OUTCOMES ─────────────────────────────────────────────── */
function Outcomes() {
  const items = [
    { icon: <Zap size={22} />, title: 'Website Becomes a Sales Rep', desc: 'High-converting branded site that captures leads and books tours — not a brochure.' },
    { icon: <Target size={22} />, title: 'Leads Stop Falling Through', desc: 'Every inquiry logged, tracked, and followed up. Automatically.' },
    { icon: <Clock size={22} />, title: 'Follow-Up On Autopilot', desc: 'SMS and email drips fire on schedule. Status-triggered messages move leads forward.' },
    { icon: <Users size={22} />, title: 'One System For Staff', desc: 'One dashboard for leads, campaigns, events, and comms. No more five apps.' },
    { icon: <Shield size={22} />, title: 'Brand Looks Stronger', desc: 'Professional web presence, branded emails, consistent messaging everywhere.' },
    { icon: <BarChart3 size={22} />, title: 'Retention Baked In', desc: 'Review requests, member follow-ups, and re-engagement on autopilot.' },
  ];
  return (
    <section id="outcomes" className="py-20 sm:py-28" style={{ background: C.bg }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <FadeIn><div className="text-center mb-14">
          <p className="text-sm font-bold tracking-[0.2em] uppercase mb-3" style={{ fontFamily: 'Manrope', color: C.accent }}>WHAT CHANGES</p>
          <h2 className="text-4xl sm:text-5xl tracking-tighter uppercase leading-none" style={{ fontFamily: 'Bebas Neue', color: C.dark }}>MORE TOURS. MORE SIGNUPS. LESS CHAOS.</h2>
        </div></FadeIn>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((item, i) => (
            <FadeIn key={i} delay={i * 60}><div className="bg-white p-7 border transition-all duration-300 hover:-translate-y-1 hover:shadow-lg h-full" style={{ borderColor: C.border }}>
              <div className="w-10 h-10 flex items-center justify-center mb-5 rounded-lg" style={{ background: '#FFF1EE', color: C.accent }}>{item.icon}</div>
              <h3 className="text-base font-bold mb-2" style={{ fontFamily: 'Manrope', color: C.dark }}>{item.title}</h3>
              <p className="text-sm leading-relaxed" style={{ fontFamily: 'Manrope', color: C.textMuted }}>{item.desc}</p>
            </div></FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── BUILT THE RIGHT WAY ─────────────────────────────────── */
function BuiltRightWay() {
  const pillars = [
    {
      kicker: 'BUILT FOR YOU',
      title: 'Software that almost fits is software that\'s losing you money.',
      body: "Mindbody was built for chains. Mailchimp was built for e-commerce. Generic schedulers were built for spas. You've been retrofitting tools that weren't made for the way you run a gym — and paying full price for the parts you don't use.",
      bold: 'HuscleOS speaks gym. Not SaaS.',
    },
    {
      kicker: 'TRUST, BAKED IN',
      title: 'Ready for the day you land a corporate wellness deal.',
      body: "The biggest revenue jump most independent gyms ever see is one B2B contract — a tech company, a school, a hospital. Those deals run through procurement. If your software stack can't answer a security questionnaire, you don't get the deal. We built the answers in from day one so you don't get DQ'd.",
      bold: "Procurement-ready, no scramble required.",
    },
    {
      kicker: 'AI-READY',
      title: 'Plugged into the way gyms will run tomorrow.',
      body: "AI assistants are about to book tours, summarize your week, and tell you which member is about to churn before they know it. That future doesn't happen if your data is locked inside three platforms that don't talk to each other. HuscleOS was built so the AI tools you and your members will use next year just work.",
      bold: 'Your data, your tools, ready for what\'s next.',
    },
  ];
  return (
    <section className="py-20 sm:py-28" style={{ background: '#fff' }} data-testid="built-right-way">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <FadeIn>
          <div className="max-w-3xl mb-14">
            <p className="text-sm font-bold tracking-[0.2em] uppercase mb-3" style={{ fontFamily: 'Manrope', color: C.accent }}>
              BUILT THE RIGHT WAY
            </p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter uppercase leading-none mb-5" style={{ fontFamily: 'Bebas Neue', color: C.dark }}>
              GYMS FIRST. NOT FEATURE FIRST.
            </h2>
            <p className="text-base leading-relaxed" style={{ fontFamily: 'Manrope', color: C.textMuted }}>
              We don't build for the spreadsheets at the top of a 50-location chain. We build for the operator who opens the gym at 5am, runs payroll on Sundays, and just wants their software to give a damn about how the business actually works.
            </p>
          </div>
        </FadeIn>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {pillars.map((p, i) => (
            <FadeIn key={i} delay={i * 80}>
              <div className="border bg-white p-7 h-full flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-lg" style={{ borderColor: C.border }} data-testid={`built-right-pillar-${i}`}>
                <p className="text-[10px] font-bold tracking-[0.2em] uppercase mb-4" style={{ fontFamily: 'Manrope', color: C.accent }}>
                  {p.kicker}
                </p>
                <h3 className="text-xl leading-tight mb-3" style={{ fontFamily: 'Manrope', fontWeight: 700, color: C.dark }}>
                  {p.title}
                </h3>
                <p className="text-sm leading-relaxed mb-4 flex-1" style={{ fontFamily: 'Manrope', color: C.textMuted }}>
                  {p.body}
                </p>
                <p className="text-sm font-bold pt-4 border-t" style={{ fontFamily: 'Manrope', color: C.dark, borderColor: C.border }}>
                  {p.bold}
                </p>
              </div>
            </FadeIn>
          ))}
        </div>
        <FadeIn delay={300}>
          <p className="text-center text-sm mt-10" style={{ fontFamily: 'Manrope', color: C.textLight }}>
            Want the full breakdown? <Link to="/blog/software-built-for-everyone-killing-your-gym" className="font-bold underline" style={{ color: C.accent }} data-testid="built-right-blog-link">Read the deep dive →</Link>
          </p>
        </FadeIn>
      </div>
    </section>
  );
}

/* ── DATA CASE — The Numbers ─────────────────────────────── */
function DataCase() {
  return (
    <section className="py-20 sm:py-28" style={{ background: C.dark }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <FadeIn>
          <p className="text-sm font-bold tracking-[0.2em] uppercase mb-4" style={{ fontFamily: 'Manrope', color: C.accent }}>THE DATA CASE</p>
          <h2 className="text-4xl sm:text-5xl tracking-tighter uppercase leading-none mb-4" style={{ fontFamily: 'Bebas Neue', color: '#fff' }}>THE NUMBERS DON'T LIE.</h2>
          <p className="text-base text-gray-400 max-w-2xl mb-12" style={{ fontFamily: 'Manrope' }}>
            81 million Americans belong to a fitness facility. Nearly 7 billion gym visits per year. And most independent gyms are still running on hope and spreadsheets. Here's what the data says about fixing that.
          </p>
        </FadeIn>
        {/* Big stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {[
            { num: '81M', label: 'Americans in fitness facilities', src: 'Health & Fitness Association, 2025' },
            { num: '21x', label: 'more likely to qualify a lead contacted in 5 min vs 30 min', src: 'MIT Lead Response Study' },
            { num: '~$780', label: 'annual value of one retained member at $65/mo avg dues', src: 'HFA Benchmarking' },
            { num: '10-12 hrs', label: 'per week saved with integrated gym tooling', src: 'Wodify & Zen Planner customer studies' },
          ].map((d, i) => (
            <FadeIn key={i} delay={i * 80}>
              <div className="p-5 border border-white/10 bg-white/[0.03]">
                <p className="text-3xl sm:text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope', color: C.accent }}>{d.num}</p>
                <p className="text-xs leading-relaxed text-gray-300 mb-2" style={{ fontFamily: 'Manrope' }}>{d.label}</p>
                <p className="text-[10px] text-gray-600" style={{ fontFamily: 'Manrope' }}>{d.src}</p>
              </div>
            </FadeIn>
          ))}
        </div>
        {/* ROI story */}
        <FadeIn delay={350}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="p-6 border border-white/10 bg-white/[0.03]">
              <p className="text-xs font-bold tracking-widest uppercase text-gray-500 mb-4">THE SPEED-TO-LEAD PROBLEM</p>
              <p className="text-sm text-gray-300 leading-relaxed mb-3" style={{ fontFamily: 'Manrope' }}>
                MIT studied 15,000+ web leads across 100,000+ call attempts over 3 years. The finding: calling a lead at 5 minutes vs 30 minutes drops your odds of contact by <strong className="text-white">100x</strong> and qualification by <strong className="text-white">21x</strong>.
              </p>
              <p className="text-sm text-gray-300 leading-relaxed" style={{ fontFamily: 'Manrope' }}>
                Waiting one hour? Contact odds fall <strong className="text-white">10x</strong>. Qualification drops <strong className="text-white">6x</strong>. If your gym's follow-up depends on someone checking their phone, you're losing before you start.
              </p>
              <p className="text-xs font-bold mt-4" style={{ color: C.accent }}>HuscleOS responds in under 60 seconds. Automatically.</p>
            </div>
            <div className="p-6 border border-white/10 bg-white/[0.03]">
              <p className="text-xs font-bold tracking-widest uppercase text-gray-500 mb-4">THE RETENTION MATH</p>
              <p className="text-sm text-gray-300 leading-relaxed mb-3" style={{ fontFamily: 'Manrope' }}>
                Average gym dues: <strong className="text-white">$65/month</strong> (HFA, 2023). Average annual retention: <strong className="text-white">66.4%</strong>. That means one retained member is worth roughly <strong className="text-white">$780/year</strong>.
              </p>
              <p className="text-sm text-gray-300 leading-relaxed mb-3" style={{ fontFamily: 'Manrope' }}>
                A 300-member gym that improves retention by just <strong className="text-white">5 percentage points</strong> keeps ~15 more members. That's <strong className="text-white">$11,700 in annual dues</strong> — before PT, retail, or onboarding fees.
              </p>
              <p className="text-xs font-bold mt-4" style={{ color: C.accent }}>HuscleOS bakes retention into the system from day one.</p>
            </div>
          </div>
        </FadeIn>
        {/* Market growth strip */}
        <FadeIn delay={450}>
          <div className="mt-8 p-4 border border-white/10 bg-white/[0.03] flex flex-wrap items-center justify-center gap-8 text-center">
            {[
              { val: '+6%', label: 'Global memberships YoY' },
              { val: '+8%', label: 'Industry revenue YoY' },
              { val: '91%', label: 'Operators expect revenue gains' },
              { val: '$500B+', label: 'US wellness spend annually' },
            ].map((d, i) => (
              <div key={i}>
                <p className="text-lg font-bold text-white" style={{ fontFamily: 'Manrope' }}>{d.val}</p>
                <p className="text-[10px] text-gray-500">{d.label}</p>
              </div>
            ))}
            <p className="text-[10px] text-gray-700 w-full mt-2">Sources: HFA, McKinsey & Company</p>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

/* ── PILLARS ─────────────────────────────────────────────── */
function Pillars() {
  const pillars = [
    { num: '01', title: 'BUILD', sub: 'Your digital front desk', desc: 'A branded website that actually converts. Mobile-first, SEO-optimized, lead capture on every page. Your gym finally looks as strong online as it feels in person.' },
    { num: '02', title: 'AUTOMATE', sub: 'Your follow-up engine', desc: 'Lead capture, follow-up sequences, staff notifications, review requests, and campaigns — all automated. Leads get nurtured. Nothing slips.' },
    { num: '03', title: 'GROW', sub: 'Your member pipeline', desc: 'A cleaner pipeline from stranger to member. Drip campaigns, status-triggered messaging, retention flows, re-engagement. One system.' },
  ];
  return (
    <section id="pillars" className="py-20 sm:py-28" style={{ background: C.dark }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <FadeIn><p className="text-sm font-bold tracking-[0.2em] uppercase mb-4" style={{ fontFamily: 'Manrope', color: C.accent }}>THE SYSTEM</p>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter uppercase leading-none mb-2" style={{ fontFamily: 'Bebas Neue', color: '#fff' }}>WE DON'T SELL SOFTWARE.</h2>
          <p className="text-lg mb-14" style={{ fontFamily: 'Manrope', color: '#9CA3AF' }}>We install systems.</p>
        </FadeIn>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {pillars.map((p, i) => (
            <FadeIn key={i} delay={i * 100}><div className="relative p-8 border overflow-hidden" style={{ background: '#1A1A1A', borderColor: '#2A2A2A' }}>
              <span className="absolute -top-6 -right-3 text-[9rem] font-bold leading-none select-none pointer-events-none" style={{ fontFamily: 'Bebas Neue', color: 'rgba(255,255,255,0.03)' }}>{p.num}</span>
              <div className="relative z-10"><span className="inline-block text-xs font-bold tracking-[0.15em] uppercase px-3 py-1 mb-5 border" style={{ fontFamily: 'Manrope', color: C.accent, borderColor: C.accent }}>{p.num}</span>
                <h3 className="text-3xl tracking-tighter uppercase mb-1" style={{ fontFamily: 'Bebas Neue', color: '#fff' }}>{p.title}</h3>
                <p className="text-sm font-semibold mb-3" style={{ fontFamily: 'Manrope', color: C.accent }}>{p.sub}</p>
                <p className="text-sm leading-relaxed text-gray-400" style={{ fontFamily: 'Manrope' }}>{p.desc}</p>
              </div>
            </div></FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── PRODUCT PREVIEW (CSS-built) ──────────────────────────── */
function ProductPreview() {
  return (
    <section className="py-20 sm:py-28 overflow-hidden" style={{ background: C.bg }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <FadeIn><div className="text-center mb-12">
          <p className="text-sm font-bold tracking-[0.2em] uppercase mb-3" style={{ fontFamily: 'Manrope', color: C.accent }}>SEE IT IN ACTION</p>
          <h2 className="text-4xl sm:text-5xl tracking-tighter uppercase leading-none mb-3" style={{ fontFamily: 'Bebas Neue', color: C.dark }}>ONE SYSTEM. EVERYTHING CONNECTED.</h2>
          <p className="text-sm max-w-lg mx-auto" style={{ fontFamily: 'Manrope', color: C.textMuted }}>Your website, CRM, automations, campaigns, and staff tools — all working together from day one.</p>
        </div></FadeIn>
        <FadeIn delay={100}>
          <div className="relative rounded-xl overflow-hidden shadow-2xl border border-gray-200" style={{ background: '#0C1420' }}>
            {/* Fake browser chrome */}
            <div className="flex items-center gap-2 px-4 py-2.5 bg-[#0C1420] border-b border-white/10">
              <div className="flex gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-red-500/60" /><div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" /><div className="w-2.5 h-2.5 rounded-full bg-green-500/60" /></div>
              <div className="flex-1 flex justify-center"><div className="px-4 py-1 rounded-md bg-white/5 text-[10px] text-gray-500" style={{ fontFamily: 'Manrope' }}>huscleos.com/staff/dashboard</div></div>
            </div>
            {/* Fake dashboard */}
            <div className="p-4 sm:p-6">
              {/* Stats row */}
              <div className="grid grid-cols-4 gap-3 mb-5">
                {[['Active Leads', '47', '#3B82F6'], ['New This Week', '12', '#F59E0B'], ['Tours Booked', '8', '#10B981'], ['Conversion Rate', '34%', '#FF2E00']].map(([label, val, color], i) => (
                  <div key={i} className="p-3 rounded-lg border border-white/10 bg-white/5">
                    <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1" style={{ fontFamily: 'Manrope' }}>{label}</p>
                    <p className="text-xl font-bold" style={{ fontFamily: 'Manrope', color }}>{val}</p>
                  </div>
                ))}
              </div>
              {/* Kanban columns */}
              <div className="grid grid-cols-4 gap-3">
                {[
                  { name: 'New Leads', color: '#3B82F6', leads: ['Sarah M.', 'James K.', 'Ella R.'] },
                  { name: 'Contacted', color: '#F59E0B', leads: ['Mike T.', 'Ana S.'] },
                  { name: 'Tour Booked', color: '#10B981', leads: ['Chris L.', 'Pat W.', 'Jordan F.'] },
                  { name: 'Member', color: '#8B5CF6', leads: ['Alex D.'] },
                ].map((col, i) => (
                  <div key={i}>
                    <div className="flex items-center gap-2 mb-2"><div className="w-2 h-2 rounded-full" style={{ background: col.color }} /><span className="text-[10px] font-bold tracking-wider uppercase text-gray-500">{col.name}</span><span className="text-[10px] ml-auto px-1.5 rounded text-gray-400 bg-white/5">{col.leads.length}</span></div>
                    <div className="space-y-1.5">
                      {col.leads.map((name, j) => (
                        <div key={j} className="p-2.5 rounded border border-white/10 bg-white/[0.03]">
                          <p className="text-xs font-medium text-gray-300">{name}</p>
                          <p className="text-[10px] text-gray-600 mt-0.5">General Membership</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

/* ── CREDIBILITY ─────────────────────────────────────────── */
function Credibility() {
  return (
    <section className="py-20 sm:py-28" style={{ background: '#fff' }}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
          <div className="lg:col-span-3">
            <FadeIn><p className="text-sm font-bold tracking-[0.2em] uppercase mb-4" style={{ fontFamily: 'Manrope', color: C.accent }}>OPERATOR BUILT</p>
              <h2 className="text-4xl sm:text-5xl tracking-tighter uppercase leading-none mb-6" style={{ fontFamily: 'Bebas Neue', color: C.dark }}>BUILT BY PEOPLE WHO<br />UNDERSTAND GYMS<br />FROM THE INSIDE.</h2>
            </FadeIn>
            <FadeIn delay={80}><p className="text-base leading-relaxed mb-4" style={{ fontFamily: 'Manrope', color: C.textMuted }}>HuscleOS wasn't dreamed up by people who have never worked a front desk, chased dead leads, or dealt with a stale website while memberships stalled.</p>
              <p className="text-base leading-relaxed font-semibold" style={{ fontFamily: 'Manrope', color: C.text }}>This system was built from the trenches. Real gym workflows. Real lead pain. Real growth needs.</p>
            </FadeIn>
          </div>
          <div className="lg:col-span-2">
            <FadeIn delay={150}><div className="border p-6 bg-gray-50" style={{ borderColor: C.border }}>
              {['Real operator experience', 'Real lead pipeline logic', 'Real retention systems', 'Real campaign automation', 'Not software tourism'].map((line, i) => (
                <div key={i} className="flex items-center gap-3 py-2.5">
                  <CheckCircle2 size={15} style={{ color: C.accent }} className="shrink-0" />
                  <p className="text-sm font-medium" style={{ fontFamily: 'Manrope', color: C.text }}>{line}</p>
                </div>
              ))}
            </div></FadeIn>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── COMPETITORS ─────────────────────────────────────────── */
function Competitors() {
  const comps = [
    { icon: <Building2 size={22} />, name: 'Big Systems', sub: 'Mindbody, ABC, etc.', pain: 'Mindbody starts at $99/mo but SMS campaigns, sales pipeline, and lead dashboard are locked behind Ultimate. You pay premium and still figure everything out.', flip: 'HuscleOS gives you the full stack from day one.', price: '$99–$400+/mo' },
    { icon: <Wrench size={22} />, name: 'DIY Stack', sub: 'PushPress + tools', pain: 'PushPress Core Pro $159/mo, Grow $329, full stack ~$559. Add Calendly, HighLevel, and you\'re managing developers, integrations, and ongoing costs.', flip: 'HuscleOS gives you the finished system — not the project.', price: '$300–$700+/mo' },
    { icon: <Palette size={22} />, name: 'Niche Platforms', sub: 'Wodify, Gymdesk, etc.', pain: 'Gymdesk $75–$200/mo. Wodify $179/mo. Lead management, campaigns, and custom websites sit on higher tiers. Built for one gym type, not yours.', flip: 'HuscleOS is built for real operators who want performance + flexibility.', price: '$75–$200+/mo' },
  ];
  return (
    <section className="py-20 sm:py-28" style={{ background: C.bg }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <FadeIn><div className="text-center mb-14"><p className="text-sm font-bold tracking-[0.2em] uppercase mb-3" style={{ fontFamily: 'Manrope', color: C.accent }}>THE DIFFERENCE</p>
          <h2 className="text-4xl sm:text-5xl tracking-tighter uppercase leading-none mb-3" style={{ fontFamily: 'Bebas Neue', color: C.dark }}>YOU'VE SEEN THE OPTIONS.</h2>
          <p className="text-base" style={{ fontFamily: 'Manrope', color: C.textMuted }}>Stop fitting your gym into someone else's system.</p>
        </div></FadeIn>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {comps.map((c, i) => (
            <FadeIn key={i} delay={i * 80}><div className="border p-7 h-full flex flex-col bg-white" style={{ borderColor: C.border }}>
              <div className="flex items-center gap-3 mb-4"><div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: '#f5f5f5', color: C.textMuted }}>{c.icon}</div>
                <div><h3 className="text-sm font-bold" style={{ fontFamily: 'Manrope', color: C.dark }}>{c.name}</h3><p className="text-xs" style={{ fontFamily: 'Manrope', color: C.textLight }}>{c.sub}</p></div>
                <span className="ml-auto text-xs font-bold px-2 py-1 rounded bg-gray-100 shrink-0" style={{ color: C.textMuted }}>{c.price}</span>
              </div>
              <p className="text-sm leading-relaxed mb-5 flex-1" style={{ fontFamily: 'Manrope', color: C.textMuted }}>{c.pain}</p>
              <div className="pt-4 border-t" style={{ borderColor: C.border }}><p className="text-sm font-bold" style={{ fontFamily: 'Manrope', color: C.accent }}>{c.flip}</p></div>
            </div></FadeIn>
          ))}
        </div>
        <FadeIn delay={300}><p className="text-center text-xs mt-6" style={{ fontFamily: 'Manrope', color: C.textLight }}>Pricing from publicly available data, 2025. Actual pricing may vary.</p></FadeIn>
      </div>
    </section>
  );
}

/* ── TEMPLATES ─────────────────────────────────────────────── */
const TEMPLATES = [
  { id: 'boutique', name: 'BOUTIQUE', sub: 'Elevated. Warm. Refined.', desc: 'For yoga studios, barre, pilates, and boutique fitness.', icon: <Heart size={22} />, gradient: 'from-rose-100 to-amber-50', ac: '#C2847A', colors: ['#F5F0EB', '#C2847A', '#3D3027', '#F9F6F3'], features: ['Warm neutrals', 'Elegant typography', 'Wellness-focused copy', 'Soft curves'] },
  { id: 'strength', name: 'STRENGTH', sub: 'Gritty. Industrial. Proven.', desc: 'For powerlifting gyms and hardcore training environments.', icon: <Dumbbell size={22} />, gradient: 'from-emerald-100 to-stone-100', ac: '#0D5D3E', colors: ['#F7F5F0', '#0D5D3E', '#FA5A5C', '#1A1A1A'], features: ['Industrial aesthetic', 'Bold display type', 'Green + coral', 'Competition-grade'] },
  { id: 'endurance', name: 'ENDURANCE', sub: 'Electric. Dynamic. Intense.', desc: 'For CrossFit boxes, HIIT studios, running clubs.', icon: <Flame size={22} />, gradient: 'from-violet-100 to-sky-50', ac: '#6366F1', colors: ['#0A0A1A', '#6366F1', '#22D3EE', '#F8FAFC'], features: ['Dark + electric', 'High-energy layout', 'Dynamic motion', 'Performance copy'] },
];

function Templates() {
  return (
    <section id="templates" className="py-20 sm:py-28" style={{ background: '#fff' }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <FadeIn><div className="text-center mb-14"><p className="text-sm font-bold tracking-[0.2em] uppercase mb-3" style={{ fontFamily: 'Manrope', color: C.accent }}>TEMPLATES</p>
          <h2 className="text-4xl sm:text-5xl tracking-tighter uppercase leading-none mb-3" style={{ fontFamily: 'Bebas Neue', color: C.dark }}>YOUR GYM. YOUR VIBE.</h2>
          <p className="text-base max-w-lg mx-auto" style={{ fontFamily: 'Manrope', color: C.textMuted }}>Each template becomes your gym's custom site, CRM, and growth engine.</p>
        </div></FadeIn>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {TEMPLATES.map((t, i) => (
            <FadeIn key={t.id} delay={i * 80}><div className="bg-white border overflow-hidden group transition-all duration-300 hover:-translate-y-1 hover:shadow-xl h-full flex flex-col" style={{ borderColor: C.border }} data-testid={`template-${t.id}`}>
              <div className={cn('h-32 flex items-center justify-center bg-gradient-to-br', t.gradient)}>
                <div className="flex gap-3">{t.colors.map((c, j) => <div key={j} className="w-9 h-9 rounded-full border-2 border-white shadow-md transition-transform group-hover:scale-110" style={{ background: c, transitionDelay: `${j * 50}ms` }} />)}</div>
              </div>
              <div className="p-6 flex flex-col flex-1">
                <div className="flex items-center gap-3 mb-3"><div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${t.ac}15`, color: t.ac }}>{t.icon}</div>
                  <div><h3 className="text-xl tracking-tighter uppercase" style={{ fontFamily: 'Bebas Neue', color: C.dark }}>{t.name}</h3><p className="text-xs font-semibold" style={{ fontFamily: 'Manrope', color: t.ac }}>{t.sub}</p></div>
                </div>
                <p className="text-sm leading-relaxed mb-4 flex-1" style={{ fontFamily: 'Manrope', color: C.textMuted }}>{t.desc}</p>
                <div className="space-y-2 pt-3 border-t" style={{ borderColor: C.border }}>{t.features.map((f, j) => <div key={j} className="flex items-center gap-2"><CheckCircle2 size={13} style={{ color: t.ac }} /><span className="text-xs font-medium" style={{ fontFamily: 'Manrope', color: C.text }}>{f}</span></div>)}</div>
                <Link to={`/huscleos/preview/${t.id}`} data-testid={`preview-${t.id}-link`} className="mt-4 flex items-center justify-center gap-2 text-sm font-bold py-3 border transition-all hover:bg-gray-50" style={{ fontFamily: 'Manrope', color: t.ac, borderColor: C.border }}>Preview Template <ChevronRight size={14} /></Link>
              </div>
            </div></FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── IDENTITY ─────────────────────────────────────────────── */
function Identity() {
  return (
    <section className="py-20 sm:py-28" style={{ background: C.dark }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
        <FadeIn><p className="text-sm font-bold tracking-[0.2em] uppercase mb-4" style={{ fontFamily: 'Manrope', color: C.accent }}>INDEPENDENT. UNITED.</p>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter uppercase leading-none mb-6" style={{ fontFamily: 'Bebas Neue', color: '#fff' }}>BUILT FOR GYMS THAT<br />REFUSE TO STAY SMALL.</h2>
          <p className="text-base leading-relaxed max-w-2xl mx-auto mb-4 text-gray-400" style={{ fontFamily: 'Manrope' }}>You don't want to be a chain. But you also don't want to run like an amateur.</p>
          <p className="text-base leading-relaxed max-w-2xl mx-auto mb-8 text-gray-200" style={{ fontFamily: 'Manrope' }}>HuscleOS gives you the structure of a large operation, the control of an independent gym, and the systems to actually scale.</p>
          <p className="text-lg font-bold text-white" style={{ fontFamily: 'Manrope' }}>All the power. None of the bureaucracy.</p>
        </FadeIn>
      </div>
    </section>
  );
}

/* ── PRICING ─────────────────────────────────────────────── */
const TIERS = [
  { name: 'LAUNCH', key: 'launch', price: '$149', per: '/mo', setup: '$750 launch setup', tagline: 'Get out of chaos mode and into control.', features: ['Branded landing page', 'Lead capture forms', 'Join / membership page', 'Contact page', 'Basic CRM pipeline', 'Lead notifications', 'Email follow-up basics', 'Mobile-friendly dashboard', 'Choose from 3 template styles'] },
  { name: 'GROW', key: 'grow', price: '$299', per: '/mo', setup: '$1,500 launch setup', tagline: 'Stop losing leads and start building real momentum.', highlight: true, features: ['Everything in Launch', 'Multi-page custom website', 'Custom-branded CRM', 'SMS + email automation', 'Lead pipeline stages', 'Status-triggered follow-ups', 'Campaign builder', 'Review request automation', 'Event management', 'Blog / SEO setup', 'Team accounts', 'Migration support'] },
  { name: 'DOMINATE', key: 'dominate', price: '$599', per: '/mo', setup: '$3,000–$5,000 launch setup', tagline: 'Run your gym like a high-performing business.', features: ['Everything in Grow', 'Fully custom site build', 'Custom sales funnel', 'Advanced campaigns', 'Premium onboarding', 'Priority support', 'Conversion consulting', 'Multi-location readiness', 'Quarterly optimization'] },
];

/* ── FOUNDING RECENT CLAIMS FEED ──────────────────────────── */
function FoundingRecentFeed() {
  const [items, setItems] = useState([]);
  const [i, setI] = useState(0);

  useEffect(() => {
    fetch(`${API}/api/huscleos/founding-recent`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => d && setItems(d.items || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (items.length < 2) return;
    const t = setInterval(() => setI((v) => (v + 1) % items.length), 4000);
    return () => clearInterval(t);
  }, [items.length]);

  if (!items.length) return null;
  const m = items[i];
  return (
    <div
      className="mt-3 px-4 py-3 flex items-center gap-3 bg-gray-50 border-t"
      style={{ borderColor: C.border }}
      data-testid="founding-recent-feed"
    >
      <span className="relative flex h-2 w-2 shrink-0">
        <span className="absolute inline-flex h-full w-full rounded-full animate-ping opacity-75" style={{ background: C.accent }} />
        <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: C.accent }} />
      </span>
      <p key={i} className="text-[11px] sm:text-xs leading-snug text-gray-700 animate-[fadeInDown_0.4s_ease-out] truncate" style={{ fontFamily: 'Manrope' }}>
        <strong style={{ color: C.dark }}>{m.display_name}</strong>
        <span className="text-gray-500"> · {m.city}</span>
        <span className="text-gray-500"> claimed the </span>
        <strong style={{ color: C.accent }}>{String(m.tier).toUpperCase()}</strong>
        <span className="text-gray-500"> tier · {m.relative}</span>
      </p>
    </div>
  );
}

function Pricing() {
  const scroll = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  const [reservingTier, setReservingTier] = React.useState(null); // tier key for modal
  const [founding, setFounding] = useState({ cap: 36, claimed: 11, remaining: 25, free_pass_ends_at: '2026-11-01T00:00:00Z' });
  const [countdown, setCountdown] = useState({ months: 0, days: 0, hours: 0, minutes: 0 });

  useEffect(() => {
    fetch(`${API}/api/huscleos/founding-status`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => d && setFounding(d))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const tick = () => {
      const end = new Date(founding.free_pass_ends_at).getTime();
      const now = Date.now();
      let diff = Math.max(0, end - now);
      const DAY = 86400000, HR = 3600000, MIN = 60000;
      const months = Math.floor(diff / (30 * DAY)); diff -= months * 30 * DAY;
      const days   = Math.floor(diff / DAY);       diff -= days * DAY;
      const hours  = Math.floor(diff / HR);        diff -= hours * HR;
      const minutes = Math.floor(diff / MIN);
      setCountdown({ months, days, hours, minutes });
    };
    tick();
    const t = setInterval(tick, 60_000); // update every minute
    return () => clearInterval(t);
  }, [founding.free_pass_ends_at]);

  const pct = Math.min(100, Math.round((founding.claimed / founding.cap) * 100));

  return (
    <section id="pricing" className="py-20 sm:py-28" style={{ background: C.bg }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <FadeIn><div className="text-center mb-10"><p className="text-sm font-bold tracking-[0.2em] uppercase mb-3" style={{ fontFamily: 'Manrope', color: C.accent }}>FOUNDING COHORT · LIVE BETA</p><h2 className="text-4xl sm:text-5xl tracking-tighter uppercase leading-none" style={{ fontFamily: 'Bebas Neue', color: C.dark }}>PICK YOUR TIER.<br/>LOCK YOUR SPOT.</h2></div></FadeIn>

        <FadeIn delay={80}>
          <div className="max-w-3xl mx-auto mb-10 p-5 border bg-white" style={{ borderColor: C.accent }} data-testid="founding-banner">
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <p className="text-xs font-bold tracking-widest uppercase" style={{ fontFamily: 'Manrope', color: C.accent }}>
                LIVE BETA · FIRST 6 MONTHS FREE
              </p>
              <p className="text-[11px] font-bold tracking-wide uppercase" style={{ fontFamily: 'Manrope', color: C.dark }} data-testid="founding-counter">
                <span style={{ color: C.accent }}>{founding.claimed}</span> of {founding.cap} founding spots claimed
              </p>
            </div>
            <div className="w-full h-1.5 bg-gray-100 mb-3 overflow-hidden" data-testid="founding-progress">
              <div className="h-full transition-all duration-700" style={{ width: `${pct}%`, background: C.accent }} />
            </div>
            <p className="text-sm leading-relaxed text-center" style={{ fontFamily: 'Manrope', color: C.text }}>
              We're in live beta. <strong>Claim a founding spot and get 6 months of HuscleOS free.</strong> We'll collect card info at signup to hold your spot — <strong className="text-gray-900">nothing is charged for 6 months</strong>. Cancel anytime in the final 30 days of your free pass. If you stay, your founding price kicks in on month 7.
            </p>
            <div className="mt-4 pt-4 border-t" style={{ borderColor: C.border }}>
              <p className="text-[10px] font-bold tracking-widest uppercase text-center mb-2" style={{ fontFamily: 'Manrope', color: C.textMuted }} data-testid="founding-window">
                Founding free pass ends in
              </p>
              <div className="flex items-center justify-center gap-3 sm:gap-5" data-testid="founding-countdown">
                {[
                  { v: countdown.months,  l: 'MO'  },
                  { v: countdown.days,    l: 'DAYS' },
                  { v: countdown.hours,   l: 'HRS' },
                  { v: countdown.minutes, l: 'MIN' },
                ].map((s, j) => (
                  <div key={j} className="flex flex-col items-center min-w-[44px]">
                    <span className="text-2xl sm:text-3xl font-bold leading-none tabular-nums" style={{ fontFamily: 'Manrope', color: C.accent }}>
                      {String(s.v).padStart(2, '0')}
                    </span>
                    <span className="text-[9px] font-bold tracking-widest mt-1" style={{ fontFamily: 'Manrope', color: C.textLight }}>{s.l}</span>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-center mt-3" style={{ fontFamily: 'Manrope', color: C.textMuted }}>
                Only <strong style={{ color: C.accent }}>{founding.remaining}</strong> founding spots remaining · Gyms first, always.
              </p>
            </div>
            <FoundingRecentFeed />
          </div>
        </FadeIn>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {TIERS.map((t, i) => (
            <FadeIn key={i} delay={i * 80}><div className={cn('flex flex-col h-full p-8 bg-white border transition-all', t.highlight && 'ring-2 ring-[#FF2E00] shadow-xl')} style={{ borderColor: t.highlight ? C.accent : C.border }} data-testid={`pricing-tier-${t.name.toLowerCase()}`}>
              {t.highlight && <span className="self-start text-[10px] font-bold tracking-widest uppercase px-3 py-1 mb-4" style={{ fontFamily: 'Manrope', background: C.accent, color: '#fff' }}>MOST POPULAR</span>}
              <h3 className="text-3xl tracking-tighter uppercase mb-1" style={{ fontFamily: 'Bebas Neue', color: C.dark }}>{t.name}</h3>
              <p className="text-sm mb-5" style={{ fontFamily: 'Manrope', color: C.textMuted }}>{t.tagline}</p>
              <div className="flex items-baseline gap-1 mb-1"><span className="text-4xl font-bold" style={{ fontFamily: 'Manrope', color: C.dark }}>{t.price}</span><span className="text-sm" style={{ color: C.textMuted }}>{t.per}</span></div>
              <p className="text-xs mb-7" style={{ fontFamily: 'Manrope', color: C.textLight }}>{t.setup}</p>
              <ul className="space-y-2.5 mb-6 flex-1">{t.features.map((f, j) => <li key={j} className="flex items-start gap-2.5"><CheckCircle2 size={14} className="shrink-0 mt-0.5" style={{ color: t.highlight ? C.accent : C.textLight }} /><span className="text-sm" style={{ fontFamily: 'Manrope', color: C.text }}>{f}</span></li>)}</ul>
              <div className="space-y-2">
                <button onClick={() => setReservingTier(t.key)} data-testid={`pricing-tier-${t.name.toLowerCase()}-subscribe`} className="w-full text-center text-sm font-bold tracking-widest uppercase py-4 transition-all hover:opacity-90" style={{ fontFamily: 'Manrope', background: t.highlight ? C.accent : C.dark, color: '#fff' }}>
                  CLAIM MY FOUNDING SPOT
                </button>
                <button onClick={() => scroll('quiz')} data-testid={`pricing-tier-${t.name.toLowerCase()}-btn`} className="w-full text-center text-xs font-bold tracking-widest uppercase py-3 transition-all hover:bg-gray-50" style={{ fontFamily: 'Manrope', color: C.textMuted, border: `1px solid ${C.border}` }}>
                  OR BUILD A DEMO FIRST
                </button>
              </div>
            </div></FadeIn>
          ))}
        </div>
        <p className="text-center text-sm mt-8" style={{ fontFamily: 'Manrope', color: C.textLight }}>Need multi-location support or custom onboarding? We can build around that.</p>
        <FadeIn delay={300}>
          <div className="mt-6 p-5 border bg-white text-center" style={{ borderColor: C.border }}>
            <p className="text-xs font-bold tracking-widest uppercase text-gray-400 mb-2">THE ROI MATH</p>
            <p className="text-sm leading-relaxed" style={{ fontFamily: 'Manrope', color: C.textMuted }}>
              Average gym dues: <strong style={{ color: C.dark }}>$65/mo</strong>. One retained member = <strong style={{ color: C.dark }}>~$780/year</strong>.
              A 300-member gym improving retention by 5% keeps ~15 more members = <strong style={{ color: C.accent }}>$11,700/year in preserved revenue</strong>.
              HuscleOS pays for itself with <strong style={{ color: C.dark }}>two extra signups</strong>.
            </p>
            <p className="text-[10px] mt-2" style={{ color: C.textLight }}>Based on HFA published dues and retention benchmarks, 2023.</p>
          </div>
        </FadeIn>
      </div>

      {reservingTier && (
        <FoundingReservationModal
          tier={TIERS.find((x) => x.key === reservingTier)}
          onClose={() => setReservingTier(null)}
          onReserved={() => {
            fetch(`${API}/api/huscleos/founding-status`)
              .then((r) => r.ok ? r.json() : null)
              .then((d) => d && setFounding(d))
              .catch(() => {});
          }}
        />
      )}
    </section>
  );
}

/* ── FOUNDING COHORT RESERVATION MODAL ────────────────────── */
function FoundingReservationModal({ tier, onClose, onReserved }) {
  const [f, setF] = useState({ name: '', email: '', gym_name: '', phone: '' });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.gym_name) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/api/huscleos/beta-reserve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...f, tier: tier.key }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'failed');
      setResult(data);
      if (onReserved) onReserved();
      toast.success(data.message || "You're in.");
    } catch (err) {
      toast.error('Something went wrong. Try again in a sec.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-10 bg-black/70 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
      data-testid="founding-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-white shadow-2xl relative my-auto"
        style={{ fontFamily: 'Manrope' }}
      >
        <button
          onClick={onClose}
          data-testid="founding-close"
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-900 p-1"
          aria-label="Close"
        >
          <XIcon size={20} />
        </button>

        <div className="p-8 sm:p-10">
          {!result ? (
            <>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#FFEDE8] mb-5">
                <Sparkles size={12} style={{ color: C.accent }} />
                <span className="text-[10px] font-bold tracking-widest uppercase" style={{ color: C.accent }}>Founding cohort · live beta</span>
              </div>
              <h2 className="text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Bebas Neue', letterSpacing: '1px', color: C.dark }}>
                LOCK IN YOUR <span style={{ color: C.accent }}>{tier.name}</span> SPOT.
              </h2>
              <p className="text-sm text-gray-600 mb-5">
                <strong className="text-gray-900">First 6 months are on us.</strong> We'll collect card info at signup to hold your founding spot — <strong className="text-gray-900">we don't charge anything for 6 months</strong>. Cancel in the final 30 days if it's not for you. Otherwise, your founding price of <strong className="text-gray-900">{tier.price}{tier.per}</strong> kicks in on month 7.
              </p>

              <form onSubmit={submit} className="space-y-3" data-testid="founding-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input required type="text" placeholder="Your name *" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
                    className="w-full px-3 py-2.5 text-sm bg-gray-50 border border-gray-200 focus:outline-none focus:border-gray-900 transition-colors"
                    data-testid="founding-name" />
                  <input required type="text" placeholder="Gym name *" value={f.gym_name} onChange={(e) => setF({ ...f, gym_name: e.target.value })}
                    className="w-full px-3 py-2.5 text-sm bg-gray-50 border border-gray-200 focus:outline-none focus:border-gray-900 transition-colors"
                    data-testid="founding-gym" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input required type="email" placeholder="Email *" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })}
                    className="w-full px-3 py-2.5 text-sm bg-gray-50 border border-gray-200 focus:outline-none focus:border-gray-900 transition-colors"
                    data-testid="founding-email" />
                  <input type="tel" placeholder="Phone (optional)" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })}
                    className="w-full px-3 py-2.5 text-sm bg-gray-50 border border-gray-200 focus:outline-none focus:border-gray-900 transition-colors"
                    data-testid="founding-phone" />
                </div>

                <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 text-[11px] leading-relaxed text-gray-600">
                  <CheckCircle2 size={14} style={{ color: C.accent }} className="shrink-0" />
                  <span>Once we exit beta, we'll set up billing with you directly — your founding price stays locked as long as you stay on the platform.</span>
                </div>

                <button type="submit" disabled={sending} data-testid="founding-submit"
                  className="w-full py-4 text-sm font-bold tracking-widest uppercase text-white transition-all disabled:opacity-50"
                  style={{ fontFamily: 'Manrope', background: C.accent }}>
                  {sending ? 'CLAIMING YOUR SPOT…' : `CLAIM MY ${tier.name} SPOT`}
                </button>
                <p className="text-[10px] text-gray-500 text-center mt-2">Gyms first. Always. — The HuscleOS team.</p>
              </form>
            </>
          ) : (
            <div className="text-center py-4" data-testid="founding-result">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-[#FFEDE8] rounded-full mb-4">
                <Sparkles size={22} style={{ color: C.accent }} />
              </div>
              <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Bebas Neue', letterSpacing: '1px', color: C.dark }}>
                YOU'RE IN.
              </h2>
              <p className="text-sm text-gray-700 mb-4 max-w-sm mx-auto">{result.message}</p>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FFEDE8] mb-6">
                <span className="text-[11px] font-bold tracking-widest uppercase" style={{ color: C.accent }}>
                  {tier.name} · {tier.price}{tier.per} · LOCKED
                </span>
              </div>
              <div>
                <button onClick={onClose} data-testid="founding-close-success"
                  className="px-8 py-3 text-sm font-bold tracking-widest uppercase text-white bg-gray-900 hover:bg-black transition-colors"
                  style={{ fontFamily: 'Manrope' }}>
                  GOT IT
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── QUIZ ─────────────────────────────────────────────────── */
const QUIZ_STEPS = [
  { id: 'type', title: "What kind of gym are you?", subtitle: "We'll match the right template and demo setup.", type: 'choice', options: [
    { value: 'boutique', label: 'Boutique / Studio', sub: 'Yoga, barre, pilates, wellness', icon: <Heart size={20} /> },
    { value: 'strength', label: 'Strength / Powerlifting', sub: 'Iron, chalk, squat racks', icon: <Dumbbell size={20} /> },
    { value: 'endurance', label: 'Endurance / HIIT', sub: 'CrossFit, HIIT, running, cycling', icon: <Flame size={20} /> },
  ]},
  { id: 'size', title: "How big is your operation?", subtitle: "Helps us scale the demo to your world.", type: 'choice', options: [
    { value: '0-100', label: 'Under 100 members', sub: 'Just getting started' }, { value: '100-300', label: '100–300 members', sub: 'Growing steadily' }, { value: '300-500', label: '300–500 members', sub: 'Established' }, { value: '500+', label: '500+ members', sub: 'Multi-location ready' },
  ]},
  { id: 'bottleneck', title: "What's holding you back?", subtitle: "We'll prioritize this in your demo.", type: 'choice', options: [
    { value: 'lead_followup', label: 'Lead follow-up', sub: 'Leads die in my inbox' }, { value: 'website', label: 'Website doesn\'t convert', sub: 'Traffic but no signups' }, { value: 'retention', label: 'Member retention', sub: 'Churn is killing us' }, { value: 'operations', label: 'Too many tools', sub: 'Staff confusion, scattered systems' },
  ]},
  { id: 'info', title: "Let's build your gym inside HuscleOS.", subtitle: "We'll create a demo tailored to your business so you can see what your gym could look like with the right system behind it.", type: 'form' },
];

function Quiz() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({ gym_type: '', member_count: '', bottleneck: '', gym_name: '', contact_name: '', email: '', phone: '' });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);
  const current = QUIZ_STEPS[step];
  const progress = ((step + 1) / QUIZ_STEPS.length) * 100;
  const fieldMap = { type: 'gym_type', size: 'member_count', bottleneck: 'bottleneck' };
  const selectChoice = (field, value) => { setData(d => ({ ...d, [field]: value })); setTimeout(() => setStep(s => Math.min(s + 1, QUIZ_STEPS.length - 1)), 300); };
  const handleSubmit = async () => {
    if (!data.gym_name || !data.email || !data.phone || !data.contact_name) { toast.error('Please fill in all required fields.'); return; }
    setSubmitting(true);
    try {
      const fd = new FormData(); Object.entries(data).forEach(([k, v]) => fd.append(k, v)); fd.append('biggest_bottleneck', data.bottleneck);
      ['city_state','current_website','current_crm','lead_volume','membership_pricing','services_offered','notes'].forEach(k => fd.append(k, ''));
      files.forEach(f => fd.append('files', f));
      const res = await fetch(`${API}/api/huscleos/leads`, { method: 'POST', body: fd }); if (!res.ok) throw new Error(); setDone(true);
    } catch { toast.error('Something went wrong.'); } finally { setSubmitting(false); }
  };
  if (done) return (
    <section id="quiz" className="py-20 sm:py-28" style={{ background: C.dark }}>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center"><FadeIn>
        <div className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ background: C.accent }}><CheckCircle2 size={32} color="#fff" /></div>
        <h2 className="text-4xl sm:text-5xl tracking-tighter uppercase leading-none mb-4" style={{ fontFamily: 'Bebas Neue', color: '#fff' }}>WE'RE BUILDING YOUR DEMO.</h2>
        <p className="text-base leading-relaxed max-w-md mx-auto mb-3 text-gray-400" style={{ fontFamily: 'Manrope' }}>Based on what you shared, we're putting together a version of HuscleOS tailored to your gym. You'll explore how your leads flow, how follow-up works, and how your website connects to everything.</p>
        <p className="text-lg font-bold mt-6 mb-2 text-white" style={{ fontFamily: 'Manrope' }}>This is what your gym looks like when it runs clean.</p>
        <p className="text-sm text-gray-600" style={{ fontFamily: 'Manrope' }}>Check your email — demo access is on the way.</p>
      </FadeIn></div>
    </section>
  );
  const iStyle = { fontFamily: 'Manrope', background: '#1A1A1A', border: '1px solid #2A2A2A' };
  return (
    <section id="quiz" className="py-20 sm:py-28" style={{ background: C.dark }}>
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        <FadeIn><div className="text-center mb-8"><p className="text-sm font-bold tracking-[0.2em] uppercase mb-3" style={{ fontFamily: 'Manrope', color: C.accent }}>BUILD YOUR DEMO</p>
          <h2 className="text-3xl sm:text-4xl tracking-tighter uppercase leading-none" style={{ fontFamily: 'Bebas Neue', color: '#fff' }}>{current.title}</h2>
          <p className="text-sm mt-2 text-gray-400" style={{ fontFamily: 'Manrope' }}>{current.subtitle}</p>
        </div></FadeIn>
        <div className="w-full h-1 rounded-full mb-8" style={{ background: '#2A2A2A' }}><div className="h-full rounded-full transition-all duration-500" style={{ width: `${progress}%`, background: C.accent }} /></div>
        {current.type === 'choice' && <div className="space-y-3">{current.options.map(opt => {
          const field = fieldMap[current.id]; const sel = data[field] === opt.value;
          return <button key={opt.value} onClick={() => selectChoice(field, opt.value)} data-testid={`quiz-${current.id}-${opt.value}`} className={cn('w-full text-left p-5 border transition-all flex items-center gap-4', sel ? 'border-[#FF2E00]' : 'border-[#2A2A2A] hover:border-[#444]')} style={{ background: sel ? '#1E1008' : '#1A1A1A' }}>
            {opt.icon && <div style={{ color: sel ? C.accent : '#6B7280' }}>{opt.icon}</div>}
            <div><p className="text-sm font-bold text-white" style={{ fontFamily: 'Manrope' }}>{opt.label}</p><p className="text-xs text-gray-400">{opt.sub}</p></div>
            {sel && <CheckCircle2 size={18} className="ml-auto shrink-0" style={{ color: C.accent }} />}
          </button>;
        })}</div>}
        {current.type === 'form' && <div className="space-y-4">
          {[{ k: 'gym_name', p: 'Gym Name *' }, { k: 'contact_name', p: 'Your Name *' }, { k: 'email', p: 'Email *', t: 'email' }, { k: 'phone', p: 'Phone *', t: 'tel' }].map(({ k, p, t }) => (
            <input key={k} type={t || 'text'} placeholder={p} value={data[k]} onChange={e => setData(d => ({ ...d, [k]: e.target.value }))} data-testid={`quiz-${k}`} className="w-full p-4 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-[#FF2E00] focus:ring-1 focus:ring-[#FF2E00] transition-colors" style={iStyle} />
          ))}
          <div><button type="button" onClick={() => fileRef.current?.click()} className="flex items-center gap-2 px-4 py-3 border text-sm text-gray-400 border-[#2A2A2A] bg-[#1A1A1A] hover:border-gray-500 transition-colors" data-testid="quiz-upload-button"><Upload size={15} /> Upload Logo / Photos <span className="text-gray-600">(optional)</span></button>
            <input ref={fileRef} type="file" multiple accept="image/*,.pdf,.svg" className="hidden" onChange={e => { setFiles(prev => [...prev, ...Array.from(e.target.files || [])].slice(0, 5)); e.target.value = ''; }} />
            {files.length > 0 && <div className="flex flex-wrap gap-2 mt-2">{files.map((f, i) => <span key={i} className="flex items-center gap-1.5 text-xs px-2.5 py-1 border text-gray-300 border-[#2A2A2A] bg-[#1A1A1A]">{f.name.slice(0, 20)}{f.name.length > 20 ? '...' : ''}<button type="button" onClick={() => setFiles(fs => fs.filter((_, j) => j !== i))} className="hover:text-red-400"><X size={11} /></button></span>)}</div>}
          </div>
          <button onClick={handleSubmit} disabled={submitting} data-testid="quiz-submit-button" className="w-full flex items-center justify-center gap-2 text-sm font-bold tracking-widest uppercase py-4 hover:opacity-90 disabled:opacity-50 transition-all" style={{ fontFamily: 'Manrope', background: C.accent, color: '#fff' }}>{submitting ? 'BUILDING YOUR DEMO...' : 'BUILD MY HUSCLEOS DEMO'} {!submitting && <ArrowRight size={16} />}</button>
        </div>}
        {step > 0 && <button onClick={() => setStep(s => s - 1)} className="flex items-center gap-1.5 mt-6 text-sm text-gray-500 hover:text-white transition-colors" style={{ fontFamily: 'Manrope' }}><ArrowLeft size={14} /> Back</button>}
      </div>
    </section>
  );
}

/* ── FINAL CTA + FOOTER ───────────────────────────────────── */
function FinalCTA() {
  const scroll = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  return (
    <section className="py-20 sm:py-28" style={{ background: C.bg }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center"><FadeIn>
        <h2 className="text-4xl sm:text-5xl lg:text-7xl tracking-tighter uppercase leading-[0.88] mb-6" style={{ fontFamily: 'Bebas Neue', color: C.dark }}>STOP RUNNING YOUR GYM<br />ON SCATTERED TOOLS AND<br /><span style={{ color: C.accent }}>CROSSED FINGERS.</span></h2>
        <p className="text-base leading-relaxed max-w-lg mx-auto mb-3" style={{ fontFamily: 'Manrope', color: C.textMuted }}>HuscleOS helps you build a stronger business behind the brand you've already worked so hard to create.</p>
        <p className="text-sm italic mb-8" style={{ fontFamily: 'Manrope', color: C.textLight }}>Your gym should come first. Not the software. Not the system. You.</p>
        <button onClick={() => scroll('quiz')} data-testid="final-cta-demo-button" className="inline-flex items-center gap-2 text-sm font-bold tracking-widest uppercase px-10 py-4 transition-all hover:shadow-lg hover:-translate-y-0.5" style={{ fontFamily: 'Manrope', background: C.accent, color: '#fff', boxShadow: '0 4px 20px rgba(255,46,0,0.2)' }}>BUILD MY DEMO <ArrowRight size={16} /></button>
      </FadeIn></div>
    </section>
  );
}
function HFooter() {
  return (
    <footer className="py-10 border-t" style={{ background: C.dark, borderColor: '#2A2A2A' }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-0"><span style={{ fontFamily: 'Bebas Neue', fontSize: '1.2rem', color: '#fff' }}>HUSCLE</span><span style={{ fontFamily: 'Bebas Neue', fontSize: '1.2rem', color: C.accent }}>OS</span></div>
          <p className="text-xs text-gray-600" style={{ fontFamily: 'Manrope' }}>We put the muscle in hustle. Not our system — yours.</p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-6 pt-4 border-t border-white/5">
          <Link to="/investors" className="text-xs text-gray-600 hover:text-gray-400 transition-colors" style={{ fontFamily: 'Manrope' }} data-testid="footer-investors">Investors</Link>
          <Link to="/blog" className="text-xs text-gray-600 hover:text-gray-400 transition-colors" style={{ fontFamily: 'Manrope' }} data-testid="footer-blog">Blog</Link>
          <Link to="/merch" className="text-xs text-gray-600 hover:text-gray-400 transition-colors" style={{ fontFamily: 'Manrope' }} data-testid="footer-merch">Merch</Link>
          <Link to="/privacy" className="text-xs text-gray-600 hover:text-gray-400 transition-colors" style={{ fontFamily: 'Manrope' }} data-testid="footer-privacy">Privacy</Link>
          <Link to="/terms" className="text-xs text-gray-600 hover:text-gray-400 transition-colors" style={{ fontFamily: 'Manrope' }} data-testid="footer-terms">Terms</Link>
          <Link to="/staff/login" className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-white/10 hover:bg-white/20 px-3 py-1.5 transition-colors" style={{ fontFamily: 'Manrope' }} data-testid="footer-portal">Portal</Link>
        </div>
      </div>
    </footer>
  );
}

/* ── PAGE ─────────────────────────────────────────────────── */
export default function HuscleOS() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div style={{ fontFamily: 'Manrope, sans-serif' }}>
      <Helmet>
        <title>HuscleOS — Gym Software, CRM & Lead Automation for Independent Gyms</title>
        <meta name="description" content="HuscleOS is the all-in-one gym software for independent operators. Built-in CRM, lead capture, follow-up automation, custom website, retention tools — replace 5 tools with one platform. See your live demo in 3 hours." />
        <meta name="keywords" content="gym software, gym CRM, gym management software, independent gym software, gym lead management, gym marketing automation, gym website builder, gym lead magnet, gym lead acquisition, gym retention software, fitness business software, Mindbody alternative, PushPress alternative, Gymdesk alternative, Wodify alternative, boutique gym software, CrossFit software" />
        <link rel="canonical" href="https://huscleos.com/" />
        <meta property="og:title" content="HuscleOS — The Operating System for Independent Gyms" />
        <meta property="og:description" content="One platform replaces MindBody + Mailchimp + ManyChat + WordPress. Built-in CRM, lead automation, custom websites, and retention tools — all in one gym software." />
        <meta property="og:url" content="https://huscleos.com/" />
        <meta property="og:type" content="website" />
      </Helmet>
      <HuscleosHeader />
      <Hero />
      <SocialProof />
      <Quiz />
      <Pain />
      <Future />
      <Outcomes />
      <BuiltRightWay />
      <DataCase />
      <Pillars />
      <ProductPreview />
      <Credibility />
      <Competitors />
      <Templates />
      <Identity />
      <Pricing />
      <FinalCTA />
      <HFooter />
    </div>
  );
}
